import { PERMISSIONS, RESOURCES } from "@/permissions/constant";
import { MODULES } from "@/permissions/modules";
import {
  Blocks,
  UsersRound,
  Briefcase,
  Contact,
  Building,
  ShieldCheck,
  User2,
  GitBranch,
  Database,
  UserCog,
  Settings,
  ScrollText,
  Palette,
  FileText,
  LayoutDashboard,
  Inbox,
  ListChecks,
  ChartColumnIncreasing,
  Logs,
  Gauge,
  SlidersHorizontal,
  MessageSquareText,
  Puzzle,
  Radar,
  BookmarkCheck,
  KeyRound,
  FolderSearch,
  Send,
  Settings2,
  MessagesSquare,
  CalendarClock,
  LayoutGrid,
  Link2,
} from "lucide-react";

export const MENU_ITEMS = [
  // Admin Menu
  {
    section: "MAIN MENU",
    icon: LayoutGrid,
    items: [
      {
        label: "Dashboard",
        icon: Blocks,
        to: "/admin/dashboard",
        // Must be real `resource.action` codes — the values the backend
        // actually returns in `permissions[]`. A bare resource string
        // ("dashboard") or a non-existent constant (PERMISSIONS.JOBS,
        // which is undefined) never matches, which hides these three
        // items from every non-wildcard user.
        permissions: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.CANDIDATE_VIEW, PERMISSIONS.JOB_VIEW],
      },
      {
        label: "Jobs",
        icon: Briefcase,
        to: "/admin/jobs",
        permissions: [PERMISSIONS.JOB_VIEW],
      },
      {
        label: "Candidates",
        icon: UsersRound,
        to: "/admin/candidates",
        permissions: [PERMISSIONS.CANDIDATE_VIEW],
      },
      {
        // Diagnostic recruitment analytics
        // This item existed before (commit 9eb782eb) but was dropped during
        // a merge conflict resolution (a8f6682f) that kept one branch's
        // section layout and discarded the other branch's items instead of
        // merging them, so it silently disappeared from the sidebar.
        label: "Statistics",
        icon: ChartColumnIncreasing,
        to: "/admin/statistics",
        permissions: [PERMISSIONS.STATISTICS_VIEW],
      },
      {
        // What this organisation has consumed. Either usage permission
        // gets you here — the page shows totals to `usage.view` and adds
        // the per-user breakdown for `usage.view_users`.
        label: "Usage",
        icon: Gauge,
        to: "/admin/usage",
        permissions: [PERMISSIONS.USAGE_VIEW, PERMISSIONS.USAGE_VIEW_USERS],
      },
      {
        // The feedback this user sent, and where each one stands — every
        // user's, with who sent it, for the tenant admin.
        label: "Feedback",
        icon: MessageSquareText,
        to: "/admin/my-feedback",
        module: MODULES.FEEDBACK,
        permissions: [PERMISSIONS.FEEDBACK_SUBMIT],
      },
    ],
  },
  {
    // Compliance is its own section:
    // Dashboard + Document Intake + Analytics are VIEW-gated; Checklists
    // additionally needs COMPLIANCE_MANAGE. Same merge (a8f6682f) dropped
    // this whole section from the sidebar even though the routes, guards,
    // and pages were all still there — reachable only by typing the URL.
    section: "COMPLIANCE",
    icon: ShieldCheck,
    items: [
      {
        label: "Dashboard",
        icon: LayoutDashboard,
        to: "/admin/compliance",
        // Exact-match only so it isn't highlighted on /intake, /checklists, /:id.
        end: true,
        // `module` hides the item when the tenant is not entitled. A
        // killed module keeps its item — the page still loads and its
        // existing data still renders.
        module: MODULES.COMPLIANCE,
        permissions: [PERMISSIONS.COMPLIANCE_VIEW],
      },
      {
        label: "Document Intake",
        icon: Inbox,
        to: "/admin/compliance/intake",
        module: MODULES.COMPLIANCE,
        permissions: [PERMISSIONS.COMPLIANCE_VIEW],
      },
      {
        label: "Checklists",
        icon: ListChecks,
        to: "/admin/compliance/checklists",
        module: MODULES.COMPLIANCE,
        permissions: [PERMISSIONS.COMPLIANCE_MANAGE],
      },
      {
        label: "Analytics",
        icon: ChartColumnIncreasing,
        to: "/admin/compliance/analytics",
        module: MODULES.COMPLIANCE,
        permissions: [PERMISSIONS.COMPLIANCE_VIEW],
      },
      {
        // The full audit trail. Its own permission, so this item is invisible
        // to an officer who works the queue but was never granted it — which
        // is the point of the separate code.
        label: "Audit Log",
        icon: ScrollText,
        to: "/admin/compliance/audit-log",
        module: MODULES.COMPLIANCE,
        permissions: [PERMISSIONS.COMPLIANCE_AUDIT_VIEW],
      },
    ],
  },
  {
    section: "OUTREACH",
    icon: Send,
    items: [
      {
        // Templates, tone and channel accounts were three entries across two
        // sections. One page with three tabs now; the old paths redirect.
        label: "Outreach Settings",
        icon: Settings2,
        to: "/admin/outreach-settings",
        module: MODULES.OUTREACH,
        permissions: [PERMISSIONS.OUTREACH_VIEW, PERMISSIONS.OUTREACH_MANAGE],
      },
      {
        label: "Outreach Sequences",
        icon: Send,
        to: "/admin/sequences",
        module: MODULES.OUTREACH,
        permissions: [PERMISSIONS.OUTREACH_VIEW, PERMISSIONS.OUTREACH_MANAGE, PERMISSIONS.OUTREACH_SEND],
      },
      {
        label: "Communications",
        icon: MessagesSquare,
        to: "/admin/communications",
        module: MODULES.OUTREACH,
        permissions: [PERMISSIONS.OUTREACH_VIEW, PERMISSIONS.OUTREACH_MANAGE, PERMISSIONS.OUTREACH_SEND],
      },
    ],
  },
  {
    section: "Access Management",
    icon: KeyRound,
    items: [
      // PERMISSIONS.ROLE / .USER / .TENANT_CONFIG don't exist (only the
      // RESOURCES entries of those names do), so these read as undefined
      // and hid the items from every non-wildcard user. Use the real
      // `resource.action` view codes.
      {
        label: "Role",
        icon: ShieldCheck,
        to: "/admin/role",
        permissions: [PERMISSIONS.ROLE_VIEW],
      },
      {
        label: "User",
        icon: User2,
        to: "/admin/user",
        permissions: [PERMISSIONS.USER_VIEW],
      },
    ],
  },
  // Sourcing is disabled — commented out, not deleted. Every related file
  // is likewise commented out; see PLAN doc "resilient-stirring-tulip".
  // {
  //   // Sourcing — permission-gated like every other section. Tenant admins
  //   // receive every active permission code, so these light up for admins
  //   // automatically; custom roles need campaign.view granted from the
  //   // Roles UI. Both items gate on VIEW only — MANAGE/RUN are enforced on
  //   // the individual actions inside the pages and on the API routes.
  //   section: "SOURCING",
  //   icon: Radar,
  //   items: [
  //     {
  //       label: "Campaigns",
  //       icon: Radar,
  //       to: "/admin/campaigns",
  //       permissions: [PERMISSIONS.CAMPAIGN_VIEW],
  //       module: MODULES.SOURCING,
  //     },
  //     {
  //       label: "Shortlist",
  //       icon: BookmarkCheck,
  //       to: "/admin/shortlist",
  //       permissions: [PERMISSIONS.CAMPAIGN_VIEW],
  //       module: MODULES.SOURCING,
  //     },
  //     {
  //       // Only meaningful for tenants the SuperAdmin has set to bring
  //       // their own API keys. The permission hides it from most users;
  //       // the page itself explains the situation to an entitled-but-
  //       // platform-managed tenant, and the API refuses either way.
  //       label: "Configure APIs",
  //       icon: KeyRound,
  //       to: "/admin/sourcing-apis",
  //       permissions: [PERMISSIONS.CAMPAIGN_CREDENTIALS_MANAGE],
  //       module: MODULES.SOURCING,
  //     },
  //   ],
  // },

  // Main Menu for Super Admin
  {
    section: "MAIN MENU",
    icon: LayoutGrid,
    items: [
      {
        label: "Dashboard",
        icon: Blocks,
        to: "/super-admin/dashboard",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "Tenant",
        icon: Building,
        to: "/super-admin/tenant",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "Permissions",
        icon: ShieldCheck,
        to: "/super-admin/permissions",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      // CV Formatting is disabled — commented out, not deleted. See PLAN
      // doc "resilient-stirring-tulip".
      // {
      //   label: "CV Templates",
      //   icon: FileText,
      //   to: "/super-admin/resume-templates",
      //   permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      // },
      {
        label: "Database Management",
        icon: Database,
        to: "/super-admin/database-management",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "SuperAdmins",
        icon: UserCog,
        to: "/super-admin/superadmins",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
    ],
  },
  {
    section: "Access Management",
    icon: KeyRound,
    items: [
      {
        label: "Role",
        icon: ShieldCheck,
        to: "/super-admin/role",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "User",
        icon: User2,
        to: "/super-admin/user",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "AuditLogs",
        icon: Logs,
        to: "/super-admin/audit-logs",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        // Fleet-wide record lookup — same dossier the tenants get, plus a
        // tenant column and the all-tenants toggle.
        label: "Record Lookup",
        icon: FolderSearch,
        to: "/super-admin/record-lookup",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        label: "Usage",
        icon: Gauge,
        to: "/super-admin/usage",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        // Fleet-wide module availability (the kill switch) and platform-wide
        // prompt-safety policy.
        label: "Platform Settings",
        icon: SlidersHorizontal,
        to: "/super-admin/platform-settings",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      {
        // Product feedback sent from every tenant.
        label: "Feedback",
        icon: MessageSquareText,
        to: "/super-admin/feedback",
        permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      },
      // Sourcing is disabled — commented out, not deleted. See PLAN doc
      // "resilient-stirring-tulip".
      // {
      //   // Our sourcing API keys — shared by every tenant not bringing its own.
      //   label: "Platform API Keys",
      //   icon: KeyRound,
      //   to: "/super-admin/sourcing-keys",
      //   permissions: [PERMISSIONS.SUPER_ADMIN_MANAGE],
      // },
    ],
  },

  {
    section: "Tenant Customization",
    icon: Settings,
    items: [
      {
        // Record lookup — the GDPR / EU AI Act dossier. Both codes ship
        // ungranted, so on a fresh database this item is invisible to
        // everyone except an admin. That is correct, not a bug.
        label: "Record Lookup",
        icon: FolderSearch,
        to: "/admin/record-lookup",
        permissions: [PERMISSIONS.RECORD_VIEW],
      },
      {
        label: "Org Settings",
        icon: Settings,
        to: "/admin/org-settings",
        permissions: [PERMISSIONS.TENANT_CONFIG_VIEW, PERMISSIONS.TENANT_CONFIG_EDIT],
      },
      {
        label: "Candidate Status",
        icon: GitBranch,
        to: "/admin/candidate-status",
        permissions: [
          PERMISSIONS.CANDIDATE_STATUS_VIEW,
          PERMISSIONS.CANDIDATE_STATUS_CREATE,
          PERMISSIONS.CANDIDATE_STATUS_UPDATE,
          PERMISSIONS.CANDIDATE_STATUS_DELETE,
        ],
      },
      {
        label: "Branding",
        icon: Palette,
        to: "/admin/branding",
        module: MODULES.BRANDING,
        permissions: [PERMISSIONS.BRANDING_VIEW, PERMISSIONS.BRANDING_MANAGE],
      },
    ],
  },

  // Extension (Sourcing's child) is disabled — commented out, not deleted.
  // See PLAN doc "resilient-stirring-tulip".
  // {
  //   section: "TOOLS",
  //   icon: Puzzle,
  //   items: [
  //     {
  //       label: "Browser Extension",
  //       icon: Puzzle,
  //       to: "/admin/chrome-extension",
  //       permissions: [PERMISSIONS.EXTENSION_VIEW],
  //       module: MODULES.EXTENSION,
  //     },
  //   ],
  // },

  {
    section: "QUICK LINKS",
    icon: Link2,
    items: [
      {
        label: "View Available Jobs",
        icon: Contact,
        to: "/user/job-listing",
        permissions: [RESOURCES.ALL],
        external: true,
      },
    ],
  },
  // Sourcing / Outreach modules — permission-gated to match the RBAC system.
  // Tenant admins receive every active permission code, so these light up for
  // admins automatically; custom roles can be granted campaign/outreach/
  // extension permissions from the Roles UI.

  // Interview Scheduling is disabled — commented out, not deleted. See
  // PLAN doc "resilient-stirring-tulip".
  // {
  //   section: "INTERVIEWS",
  //   icon: CalendarClock,
  //   items: [
  //     {
  //       label: "Interview Scheduling",
  //       icon: CalendarClock,
  //       to: "/admin/interview-scheduling",
  //       // `module` hides the item when the tenant is not entitled. A
  //       // killed module keeps its item — the page still loads and its
  //       // existing data still renders.
  //       module: MODULES.INTERVIEW_SCHEDULING,
  //       permissions: [
  //         PERMISSIONS.INTERVIEW_VIEW,
  //         PERMISSIONS.INTERVIEW_SCHEDULE,
  //         PERMISSIONS.INTERVIEW_RESCHEDULE,
  //         PERMISSIONS.INTERVIEW_CANCEL,
  //         PERMISSIONS.INTERVIEW_MANAGE,
  //       ],
  //     },
  //   ],
  // },
];
